// ProgressUpload.js
import React, { useEffect, useState } from 'react'
import { styled } from '@mui/system'

import { Typography } from '@mui/material'
import { useDownload } from 'hooks/useDownload'
import { Box } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { getTasks } from 'api/graphs'
import { IconExclamationCircle, IconX, IconCheck } from '@tabler/icons-react'
import IconButton from '@mui/material/IconButton'
import { useTheme } from '@mui/material/styles'

const ProgressDownloadContainer = styled('div')({
  position: 'absolute',
  right: 260,
  bottom: 10,
  zIndex: 9999,
  width: 300,
  height: 'auto',
  backgroundColor: '#fff',
  boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
  borderRadius: 8,
  border: '1px solid #2C2C2C',
  display: 'none', // Initially hidden
  transform: 'translateY(10px)', // Slightly translate down for effect
  transition: 'opacity 0.3s ease, transform 0.3s ease', // Transition effects
  '&.visible': {
    display: 'block',
    transform: 'translateY(0)'
  }
})

const ProgressDownload = () => {
  const { tasks, updateTaskById, clearAllTasks, removeTaskById } = useDownload()
  const isVisible = tasks.length > 0
  const theme = useTheme()

  const truncateText = (text, maxLength = 30) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + '...'
  }

  const downloadFile = (url, name) => {
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', name)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const fetchTaskStatus = async () => {
    tasks.forEach(async (task) => {
      const data = await getTasks({ name: task.id })
      const curTask = data[0]
      if (!curTask) return

      if (curTask.status?.phase === 'Succeeded' && task.status !== 'Succeeded') {
        downloadFile(task.outputPath, task.itemId)
        removeTaskById(task.id)
      } else {
        updateTaskById({
          id: task.id,
          status: curTask.status?.phase
        })
      }
    })
  }

  useEffect(() => {
    if (!isVisible) return

    const interval = setInterval(() => {
      fetchTaskStatus()
    }, 5000)

    return () => clearInterval(interval)
  }, [isVisible, tasks])

  return (
    <ProgressDownloadContainer className={isVisible ? 'visible' : ''} sx={{ backgroundColor: theme.palette.background.paper }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" align="center" sx={{ px: 2, py: 1 }}>
          Download Item
        </Typography>
        <IconButton aria-label="delete" onClick={clearAllTasks}>
          <IconX size={12} />
        </IconButton>
      </Box>

      <Box sx={{ width: '100%', px: 2, py: 1, overflowY: 'auto', gap: 2, display: 'flex', flexDirection: 'column' }}>
        {tasks.map((task) => (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }} key={task.id}>
            <Typography>{truncateText(task.itemId)}</Typography>
            {task.status === 'Running' ? (
              <CircularProgress size={12} />
            ) : task.status === 'Failed' ? (
              <IconExclamationCircle size={12} />
            ) : (
              <IconCheck size={12} color="green" />
            )}
          </Box>
        ))}
      </Box>
    </ProgressDownloadContainer>
  )
}

export default ProgressDownload
